/**
 * =============================================================================
 * Institutions Management Page
 * =============================================================================
 *
 * CRUD interface for managing institutions stored in the local database.
 *
 * FE #27 / BE #61: creating an institution provisions its first institution
 * administrator by default (invite e-mail to the CLINIC portal); an existing
 * user is offered for assignment (+ notification). Existing institutions get
 * an "Add admin" action. Mail problems are shown, never hidden.
 * The institution's login/notification e-mails are contact fields only.
 *
 * Access: system_admin, rehapiano_admin
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import DataTable, { Column } from '../components/DataTable'
import Modal from '../components/Modal'
import {
  AdminProvisionFields,
  OutcomeBanner,
  ProvisionConflictPanel,
} from '../components/InstitutionAdminFields'
import {
  type AdminProvisionState,
  describeAdminResult,
  emptyAdminProvision,
  type OutcomeMessage,
  toProvisionPayload,
} from '../utils/institutionAdmin'
import {
  Institution,
  InstitutionCreate,
  InstitutionUpdate,
  institutionsApi,
} from '../services/api'
import { provisionConflict, type ProvisionConflict, ut } from '../utils/usersI18n'

const errorText = (err: unknown, fallback: string): string => {
  const error = err as { response?: { status?: number; data?: { detail?: unknown } } }
  if (error?.response?.status === 409) return ut('institutionDuplicate')
  const detail = error?.response?.data?.detail
  return typeof detail === 'string' && detail ? detail : fallback
}

interface InstitutionFormProps {
  initial?: Institution | null
  onSubmit: (payload: InstitutionCreate | InstitutionUpdate) => void
  onCancel: () => void
  loading?: boolean
  /** Conflict panel rendered above the buttons (create only). */
  conflictSlot?: React.ReactNode
  error?: string | null
}

const InstitutionForm: React.FC<InstitutionFormProps> = ({
  initial,
  onSubmit,
  onCancel,
  loading,
  conflictSlot,
  error,
}) => {
  const isEdit = !!initial
  const [formData, setFormData] = useState({
    code: initial?.code || '',
    institution_name: initial?.institution_name || '',
    institution_type: initial?.institution_type || '',
    address: initial?.address || '',
    country: initial?.country || 'SK',
    login_email: initial?.login_email || '',
    notification_email: initial?.notification_email || '',
    status: initial?.status || 'active',
  })
  // First institution admin (create only, default ON). The admin e-mail
  // follows the contact e-mail until the user edits it.
  const [withAdmin, setWithAdmin] = useState(true)
  const [admin, setAdmin] = useState<AdminProvisionState>(emptyAdminProvision())
  const [adminEmailTouched, setAdminEmailTouched] = useState(false)
  const adminValue = adminEmailTouched ? admin : { ...admin, email: formData.login_email }

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    if (isEdit) {
      const payload: InstitutionUpdate = {
        institution_name: formData.institution_name.trim(),
        institution_type: formData.institution_type || undefined,
        address: formData.address || undefined,
        country: formData.country || undefined,
        login_email: formData.login_email.trim(),
        notification_email: formData.notification_email || undefined,
        status: formData.status as 'active' | 'inactive',
      }
      onSubmit(payload)
      return
    }

    const payload: InstitutionCreate = {
      code: formData.code.trim(),
      institution_name: formData.institution_name.trim(),
      institution_type: formData.institution_type || undefined,
      address: formData.address || undefined,
      country: formData.country || undefined,
      login_email: formData.login_email.trim(),
      notification_email: formData.notification_email || undefined,
      status: formData.status as 'active' | 'inactive',
      first_admin: withAdmin
        ? toProvisionPayload(adminValue, formData.login_email)
        : { create: false },
    }

    onSubmit(payload)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {!isEdit && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{ut('fieldCode')}</label>
          <input
            className="input"
            data-testid="institution-code"
            value={formData.code}
            onChange={(e) => setFormData({ ...formData, code: e.target.value })}
            placeholder="hospital-bratislava"
            required
          />
          <p className="text-xs text-gray-500 mt-1">{ut('fieldCodeHint')}</p>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{ut('fieldInstitutionName')}</label>
        <input
          className="input"
          data-testid="institution-name"
          value={formData.institution_name}
          onChange={(e) => setFormData({ ...formData, institution_name: e.target.value })}
          placeholder="FN Bratislava"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{ut('fieldInstitutionType')}</label>
        <input
          className="input"
          value={formData.institution_type}
          onChange={(e) => setFormData({ ...formData, institution_type: e.target.value })}
          placeholder="hospital, clinic, research"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{ut('fieldAddress')}</label>
        <input
          className="input"
          value={formData.address}
          onChange={(e) => setFormData({ ...formData, address: e.target.value })}
          placeholder="Klemensova 11, Bratislava"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{ut('fieldCountry')}</label>
          <input
            className="input"
            value={formData.country}
            onChange={(e) => setFormData({ ...formData, country: e.target.value.toUpperCase() })}
            placeholder="SK"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{ut('fieldStatus')}</label>
          <select
            className="input"
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.target.value as 'active' | 'inactive' })}
          >
            <option value="active">{ut('statusActive')}</option>
            <option value="inactive">{ut('statusInactive')}</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{ut('contactEmailLabel')}</label>
        <input
          type="email"
          className="input"
          data-testid="institution-login-email"
          value={formData.login_email}
          onChange={(e) => setFormData({ ...formData, login_email: e.target.value })}
          placeholder="kontakt@nemocnica.sk"
          required
        />
        <p className="text-xs text-gray-500 mt-1">{ut('contactEmailHint')}</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{ut('notificationEmailLabel')}</label>
        <input
          type="email"
          className="input"
          value={formData.notification_email}
          onChange={(e) => setFormData({ ...formData, notification_email: e.target.value })}
          placeholder="alerts@hospital.com"
        />
      </div>

      {!isEdit && (
        <fieldset className="space-y-3 rounded-lg border border-gray-200 p-3">
          <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
            {ut('firstAdminTitle')}
          </legend>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300"
              data-testid="first-admin-toggle"
              checked={withAdmin}
              onChange={(e) => setWithAdmin(e.target.checked)}
            />
            {ut('firstAdminToggle')}
          </label>
          {withAdmin && (
            <AdminProvisionFields
              value={adminValue}
              testIdPrefix="first"
              onChange={(next) => {
                if (next.email !== adminValue.email) setAdminEmailTouched(true)
                setAdmin(next)
              }}
            />
          )}
          <p className="text-xs text-blue-700">{ut('clinicLoginHint')}</p>
        </fieldset>
      )}

      {/* Errors sit next to the buttons: the form is taller than the viewport. */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
          {error}
        </div>
      )}
      {conflictSlot}

      <div className="flex justify-end gap-3 pt-4 border-t">
        <button type="button" className="btn-secondary" onClick={onCancel} disabled={loading}>
          {ut('cancel')}
        </button>
        <button type="submit" className="btn-primary" disabled={loading} data-testid="institution-submit">
          {loading ? ut('saving') : isEdit ? ut('update') : ut('create')}
        </button>
      </div>
    </form>
  )
}

const StatusBadge: React.FC<{ status: Institution['status'] }> = ({ status }) => {
  return (
    <span
      className={`badge ${status === 'active' ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-200 text-gray-700'}`}
    >
      {status === 'active' ? ut('statusActive') : ut('statusInactive')}
    </span>
  )
}

const InstitutionsPage: React.FC = () => {
  const queryClient = useQueryClient()
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [isEditOpen, setIsEditOpen] = useState(false)
  const [selectedInstitution, setSelectedInstitution] = useState<Institution | null>(null)
  const [outcome, setOutcome] = useState<OutcomeMessage | null>(null)

  // Create flow: the last payload is kept so a 409 "user exists" can be
  // resolved by re-submitting with assign_existing / without an admin.
  const [pendingCreate, setPendingCreate] = useState<InstitutionCreate | null>(null)
  const [createConflict, setCreateConflict] = useState<ProvisionConflict | null>(null)
  const [createError, setCreateError] = useState<string | null>(null)

  // "Add admin" flow for an existing institution.
  const [adminTarget, setAdminTarget] = useState<Institution | null>(null)
  const [adminForm, setAdminForm] = useState<AdminProvisionState>(emptyAdminProvision())
  const [adminConflict, setAdminConflict] = useState<ProvisionConflict | null>(null)
  const [adminError, setAdminError] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['institutions'],
    queryFn: () => institutionsApi.list(),
  })

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['institutions'] })
    await queryClient.invalidateQueries({ queryKey: ['institution-users'] })
  }

  const closeCreate = () => {
    setIsCreateOpen(false)
    setPendingCreate(null)
    setCreateConflict(null)
    setCreateError(null)
  }

  const createMutation = useMutation({
    mutationFn: (payload: InstitutionCreate) => institutionsApi.create(payload),
    onMutate: (payload) => {
      setPendingCreate(payload)
      setCreateConflict(null)
      setCreateError(null)
    },
    onSuccess: async (response) => {
      closeCreate()
      const created = ut('institutionCreated', { name: response.data.institution_name })
      const admin = response.data.first_admin
      if (admin) {
        const result = describeAdminResult(admin)
        setOutcome({ severity: result.severity, text: `${created} ${result.text}` })
      } else {
        setOutcome({ severity: 'success', text: created })
      }
      await refresh()
    },
    onError: (err) => {
      const conflict = provisionConflict(err)
      if (conflict) {
        setCreateConflict(conflict)
        return
      }
      setCreateError(errorText(err, ut('institutionSaveFailed')))
    },
  })

  const updateMutation = useMutation({
    mutationFn: (params: { id: string; payload: InstitutionUpdate }) =>
      institutionsApi.update(params.id, params.payload),
    onSuccess: async () => {
      setIsEditOpen(false)
      setSelectedInstitution(null)
      await refresh()
    },
  })

  const statusMutation = useMutation({
    mutationFn: (params: { id: string; status: Institution['status'] }) =>
      institutionsApi.updateStatus(params.id, { status: params.status }),
    onSuccess: async () => {
      await refresh()
    },
  })

  const closeAddAdmin = () => {
    setAdminTarget(null)
    setAdminConflict(null)
    setAdminError(null)
  }

  const addAdminMutation = useMutation({
    mutationFn: (params: { id: string; assignExisting: boolean }) =>
      institutionsApi.addAdmin(
        params.id,
        toProvisionPayload(adminForm, adminTarget?.login_email || '', params.assignExisting),
      ),
    onMutate: () => {
      setAdminConflict(null)
      setAdminError(null)
    },
    onSuccess: async (response) => {
      closeAddAdmin()
      setOutcome(describeAdminResult(response.data))
      await refresh()
    },
    onError: (err) => {
      const conflict = provisionConflict(err)
      if (conflict) {
        setAdminConflict(conflict)
        return
      }
      setAdminError(errorText(err, ut('adminCreateFailed')))
    },
  })

  const openAddAdmin = (inst: Institution) => {
    setOutcome(null)
    setAdminForm(emptyAdminProvision(inst.login_email || ''))
    setAdminConflict(null)
    setAdminError(null)
    setAdminTarget(inst)
  }

  const columns: Column<Institution>[] = [
    {
      key: 'institution_name',
      header: ut('colInstitution'),
      sortable: true,
      sortFn: (a, b) => a.institution_name.localeCompare(b.institution_name),
      searchable: true,
      searchFn: (item, query) =>
        item.institution_name.toLowerCase().includes(query) || item.code.toLowerCase().includes(query),
      render: (inst) => (
        <div>
          <div className="font-medium text-gray-900">{inst.institution_name}</div>
          <div className="text-xs text-gray-500">{inst.code}</div>
        </div>
      ),
    },
    {
      key: 'type',
      header: ut('colType'),
      render: (inst) => inst.institution_type || '-',
    },
    {
      key: 'country',
      header: ut('colCountry'),
      render: (inst) => inst.country || 'SK',
    },
    {
      key: 'login_email',
      header: ut('colContact'),
      render: (inst) => inst.login_email,
    },
    {
      key: 'status',
      header: ut('colStatus'),
      render: (inst) => <StatusBadge status={inst.status} />,
    },
    {
      key: 'devices',
      header: ut('colDevices'),
      render: (inst) => inst.devices_count ?? 0,
    },
    {
      key: 'users',
      header: ut('colUsers'),
      render: (inst) => inst.users_count ?? 0,
    },
    {
      key: 'actions',
      header: ut('colActions'),
      render: (inst) => (
        <div className="flex flex-wrap items-center gap-2">
          <button
            className="btn-secondary text-xs"
            onClick={() => {
              setSelectedInstitution(inst)
              setIsEditOpen(true)
            }}
          >
            {ut('edit')}
          </button>
          <button
            className="btn-secondary text-xs"
            data-testid={`add-admin-${inst.id}`}
            onClick={() => openAddAdmin(inst)}
          >
            {ut('addAdminAction')}
          </button>
          <button
            className="btn-secondary text-xs"
            onClick={() =>
              statusMutation.mutate({
                id: inst.id,
                status: inst.status === 'active' ? 'inactive' : 'active',
              })
            }
          >
            {inst.status === 'active' ? ut('deactivate') : ut('activate')}
          </button>
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{ut('institutionsTitle')}</h2>
          <p className="text-sm text-gray-600">{ut('institutionsIntro')}</p>
          <p className="text-xs text-blue-700">{ut('clinicLoginHint')}</p>
        </div>
        <button
          className="btn-primary"
          data-testid="open-create-institution"
          onClick={() => {
            setOutcome(null)
            setIsCreateOpen(true)
          }}
        >
          {ut('addInstitution')}
        </button>
      </div>

      <OutcomeBanner outcome={outcome} onClose={() => setOutcome(null)} />

      <DataTable
        columns={columns}
        data={data?.data || []}
        loading={isLoading}
        getRowKey={(inst) => inst.id}
        emptyMessage={ut('noInstitutions')}
      />

      <Modal isOpen={isCreateOpen} onClose={closeCreate} title={ut('createInstitutionTitle')} size="lg">
        <InstitutionForm
          onSubmit={(payload) => createMutation.mutate(payload as InstitutionCreate)}
          onCancel={closeCreate}
          loading={createMutation.isPending}
          error={createError}
          conflictSlot={
            createConflict && pendingCreate ? (
              <ProvisionConflictPanel
                conflict={createConflict}
                busy={createMutation.isPending}
                onCancel={() => setCreateConflict(null)}
                onAssign={() =>
                  createMutation.mutate({
                    ...pendingCreate,
                    first_admin: { ...pendingCreate.first_admin, assign_existing: true },
                  })
                }
                onSkip={() => createMutation.mutate({ ...pendingCreate, first_admin: { create: false } })}
              />
            ) : null
          }
        />
      </Modal>

      <Modal
        isOpen={isEditOpen}
        onClose={() => {
          setIsEditOpen(false)
          setSelectedInstitution(null)
        }}
        title={ut('editInstitutionTitle')}
      >
        <InstitutionForm
          initial={selectedInstitution}
          onSubmit={(payload) => {
            if (!selectedInstitution) return
            updateMutation.mutate({ id: selectedInstitution.id, payload: payload as InstitutionUpdate })
          }}
          onCancel={() => {
            setIsEditOpen(false)
            setSelectedInstitution(null)
          }}
          loading={updateMutation.isPending}
        />
      </Modal>

      <Modal
        isOpen={adminTarget !== null}
        onClose={closeAddAdmin}
        title={adminTarget ? `${ut('addAdminTitle')}: ${adminTarget.institution_name}` : ut('addAdminTitle')}
        size="lg"
      >
        {adminTarget && (
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault()
              addAdminMutation.mutate({ id: adminTarget.id, assignExisting: false })
            }}
          >
            {adminError && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
                {adminError}
              </div>
            )}
            <p className="text-xs text-blue-700">{ut('clinicLoginHint')}</p>
            <AdminProvisionFields value={adminForm} onChange={setAdminForm} testIdPrefix="add" />
            {adminConflict && (
              <ProvisionConflictPanel
                conflict={adminConflict}
                busy={addAdminMutation.isPending}
                onCancel={() => setAdminConflict(null)}
                onAssign={() => addAdminMutation.mutate({ id: adminTarget.id, assignExisting: true })}
              />
            )}
            <div className="flex justify-end gap-2 border-t pt-3">
              <button type="button" className="btn-secondary" onClick={closeAddAdmin}>
                {ut('cancel')}
              </button>
              <button
                type="submit"
                className="btn-primary"
                data-testid="add-admin-submit"
                disabled={addAdminMutation.isPending}
              >
                {addAdminMutation.isPending ? ut('submitting') : ut('addAdminSubmit')}
              </button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  )
}

export default InstitutionsPage
